import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper to format currency
const formatCurrency = (amount) => {
  return `Rs. ${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Number to words conversion for Indian currency
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
  };

  if (num === 0) return 'Zero';
  
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = Math.floor(num % 1000);
  const paise = Math.round((num - Math.floor(num)) * 100);

  let words = '';
  if (crore) words += convertLessThanThousand(crore) + ' Crore ';
  if (lakh) words += convertLessThanThousand(lakh) + ' Lakh ';
  if (thousand) words += convertLessThanThousand(thousand) + ' Thousand ';
  if (remainder) words += convertLessThanThousand(remainder);
  
  words = words.trim() + ' Rupees';
  if (paise) words += ' and ' + convertLessThanThousand(paise) + ' Paise';
  words += ' Only';
  
  return words;
};

/**
 * Generate PDF for Bill (GST/Cash Invoice)
 */
export const generateBillPDF = async (bill, businessInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const primaryColor = [41, 98, 255]; // Blue
  const darkGray = [50, 50, 50];
  const lightGray = [100, 100, 100];
  
  let y = 15;
  
  // Header Background
  doc.setFillColor(245, 247, 250);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Company Name
  doc.setFontSize(20);
  doc.setTextColor(...primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(businessInfo?.business_name || 'SUNTECH ENTERPRISES', 15, y + 5);
  
  // Company Details
  doc.setFontSize(9);
  doc.setTextColor(...lightGray);
  doc.setFont('helvetica', 'normal');
  const address = businessInfo?.address || 'Coimbatore, Tamil Nadu';
  doc.text(address, 15, y + 12);
  
  if (businessInfo?.gstin) {
    doc.text(`GSTIN: ${businessInfo.gstin}`, 15, y + 17);
  }
  if (businessInfo?.phone) {
    doc.text(`Phone: ${businessInfo.phone}`, 15, y + 22);
  }
  
  // Invoice Title
  doc.setFontSize(24);
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  const invoiceType = bill.bill_type === 'GST' ? 'TAX INVOICE' : 'CASH BILL';
  doc.text(invoiceType, pageWidth - 15, y + 10, { align: 'right' });
  
  // Invoice Number and Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice No: ${bill.bill_no}`, pageWidth - 15, y + 18, { align: 'right' });
  doc.text(`Date: ${bill.bill_date}`, pageWidth - 15, y + 24, { align: 'right' });
  if (bill.due_date) {
    doc.text(`Due Date: ${bill.due_date}`, pageWidth - 15, y + 30, { align: 'right' });
  }
  
  y = 55;
  
  // Bill To Section
  doc.setFillColor(41, 98, 255);
  doc.rect(15, y, 85, 7, 'F');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO', 18, y + 5);
  
  y += 12;
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text(bill.customer?.company_name || 'Customer', 15, y);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  y += 5;
  if (bill.customer?.address) {
    doc.text(bill.customer.address, 15, y);
    y += 4;
  }
  doc.text(`${bill.customer?.city || ''}, ${bill.customer?.state || 'Tamil Nadu'}`, 15, y);
  y += 4;
  doc.text(`Phone: ${bill.customer?.mobile || ''}`, 15, y);
  y += 4;
  if (bill.customer?.gst_number || bill.customer?.gstin) {
    doc.text(`GSTIN: ${bill.customer?.gst_number || bill.customer?.gstin}`, 15, y);
  }
  
  y = 55;
  
  // Ship To (if different) - Right side
  doc.setFillColor(41, 98, 255);
  doc.rect(110, y, 85, 7, 'F');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('SHIP TO', 113, y + 5);
  
  y += 12;
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text(bill.customer?.company_name || 'Same as Bill To', 110, y);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  y += 5;
  doc.text(`${bill.customer?.city || ''}, ${bill.customer?.state || 'Tamil Nadu'}`, 110, y);
  
  y = 105;
  
  // Items Table
  const tableColumns = [
    { header: '#', dataKey: 'sno' },
    { header: 'Description', dataKey: 'description' },
    { header: 'HSN', dataKey: 'hsn' },
    { header: 'Qty', dataKey: 'qty' },
    { header: 'Rate', dataKey: 'rate' },
  ];
  
  if (bill.bill_type === 'GST') {
    if (bill.is_igst) {
      tableColumns.push(
        { header: 'IGST', dataKey: 'igst' }
      );
    } else {
      tableColumns.push(
        { header: 'CGST', dataKey: 'cgst' },
        { header: 'SGST', dataKey: 'sgst' }
      );
    }
  }
  tableColumns.push({ header: 'Amount', dataKey: 'amount' });
  
  const tableData = bill.items?.map((item, index) => {
    const row = {
      sno: index + 1,
      description: item.description,
      hsn: item.hsn_code || '-',
      qty: item.quantity,
      rate: formatCurrency(item.unit_price),
    };
    
    if (bill.bill_type === 'GST') {
      if (bill.is_igst) {
        row.igst = `${item.igst_percent}%`;
      } else {
        row.cgst = `${item.cgst_percent}%`;
        row.sgst = `${item.sgst_percent}%`;
      }
    }
    row.amount = formatCurrency(item.total);
    return row;
  }) || [];
  
  autoTable(doc, {
    startY: y,
    head: [tableColumns.map(col => col.header)],
    body: tableData.map(row => tableColumns.map(col => row[col.dataKey])),
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: darkGray
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: 15, right: 15 }
  });
  
  y = doc.lastAutoTable.finalY + 10;
  
  // Totals Section
  const totalsStartX = pageWidth - 80;
  
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  
  // Subtotal
  doc.text('Subtotal:', totalsStartX, y);
  doc.text(formatCurrency(bill.subtotal), pageWidth - 15, y, { align: 'right' });
  y += 6;
  
  // Discount
  if (bill.total_discount > 0) {
    doc.text('Discount:', totalsStartX, y);
    doc.setTextColor(220, 50, 50);
    doc.text(`-${formatCurrency(bill.total_discount)}`, pageWidth - 15, y, { align: 'right' });
    doc.setTextColor(...darkGray);
    y += 6;
  }
  
  // GST
  if (bill.bill_type === 'GST') {
    if (bill.is_igst) {
      doc.text(`IGST:`, totalsStartX, y);
      doc.text(formatCurrency(bill.total_igst), pageWidth - 15, y, { align: 'right' });
      y += 6;
    } else {
      doc.text(`CGST:`, totalsStartX, y);
      doc.text(formatCurrency(bill.total_cgst), pageWidth - 15, y, { align: 'right' });
      y += 6;
      
      doc.text(`SGST:`, totalsStartX, y);
      doc.text(formatCurrency(bill.total_sgst), pageWidth - 15, y, { align: 'right' });
      y += 6;
    }
  }
  
  // Total
  doc.setFillColor(41, 98, 255);
  doc.rect(totalsStartX - 5, y - 3, pageWidth - totalsStartX + 5 - 10, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL:', totalsStartX, y + 4);
  doc.text(formatCurrency(bill.net_amount), pageWidth - 15, y + 4, { align: 'right' });
  
  y += 18;
  
  // Amount in Words
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Amount in Words:', 15, y);
  doc.setFont('helvetica', 'bold');
  doc.text(numberToWords(bill.net_amount), 15, y + 5);
  
  y += 20;
  
  // Bank Details (if available)
  if (businessInfo?.bank_name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Bank Details:', 15, y);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    y += 6;
    doc.text(`Bank: ${businessInfo.bank_name}`, 15, y);
    y += 4;
    doc.text(`A/C No: ${businessInfo.account_number}`, 15, y);
    y += 4;
    doc.text(`IFSC: ${businessInfo.ifsc_code}`, 15, y);
  }
  
  // Notes
  if (bill.notes) {
    y += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Notes:', 15, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    y += 5;
    const splitNotes = doc.splitTextToSize(bill.notes, pageWidth - 30);
    doc.text(splitNotes, 15, y);
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 25;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, footerY, pageWidth - 15, footerY);
  
  doc.setFontSize(8);
  doc.setTextColor(...lightGray);
  doc.text('This is a computer generated invoice.', pageWidth / 2, footerY + 8, { align: 'center' });
  
  // Signature area
  doc.setTextColor(...darkGray);
  doc.setFontSize(9);
  doc.text('For ' + (businessInfo?.business_name || 'SUNTECH ENTERPRISES'), pageWidth - 50, footerY - 15, { align: 'center' });
  doc.text('Authorized Signatory', pageWidth - 50, footerY - 5, { align: 'center' });
  
  return doc;
};

/**
 * Generate PDF for Quotation
 */
export const generateQuotationPDF = async (quotation, businessInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const primaryColor = [76, 175, 80]; // Green for quotations
  const darkGray = [50, 50, 50];
  const lightGray = [100, 100, 100];
  
  let y = 15;
  
  // Header Background
  doc.setFillColor(245, 250, 245);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Company Name
  doc.setFontSize(20);
  doc.setTextColor(...primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(businessInfo?.business_name || 'SUNTECH ENTERPRISES', 15, y + 5);
  
  // Company Details
  doc.setFontSize(9);
  doc.setTextColor(...lightGray);
  doc.setFont('helvetica', 'normal');
  const address = businessInfo?.address || 'Coimbatore, Tamil Nadu';
  doc.text(address, 15, y + 12);
  
  if (businessInfo?.gstin) {
    doc.text(`GSTIN: ${businessInfo.gstin}`, 15, y + 17);
  }
  if (businessInfo?.phone) {
    doc.text(`Phone: ${businessInfo.phone}`, 15, y + 22);
  }
  
  // Quotation Title
  doc.setFontSize(24);
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION', pageWidth - 15, y + 10, { align: 'right' });
  
  // Quotation Number and Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Quotation No: ${quotation.quotation_no}`, pageWidth - 15, y + 18, { align: 'right' });
  doc.text(`Date: ${quotation.quotation_date}`, pageWidth - 15, y + 24, { align: 'right' });
  if (quotation.valid_until) {
    doc.text(`Valid Until: ${quotation.valid_until}`, pageWidth - 15, y + 30, { align: 'right' });
  }
  
  y = 55;
  
  // To Section
  doc.setFillColor(...primaryColor);
  doc.rect(15, y, 85, 7, 'F');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION FOR', 18, y + 5);
  
  y += 12;
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text(quotation.customer?.company_name || 'Customer', 15, y);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  y += 5;
  if (quotation.customer?.address) {
    doc.text(quotation.customer.address, 15, y);
    y += 4;
  }
  doc.text(`${quotation.customer?.city || ''}, ${quotation.customer?.state || 'Tamil Nadu'}`, 15, y);
  y += 4;
  doc.text(`Phone: ${quotation.customer?.mobile || ''}`, 15, y);
  y += 4;
  if (quotation.customer?.email) {
    doc.text(`Email: ${quotation.customer.email}`, 15, y);
  }
  
  y = 100;
  
  // Items Table
  const tableColumns = [
    { header: '#', dataKey: 'sno' },
    { header: 'Description', dataKey: 'description' },
    { header: 'HSN', dataKey: 'hsn' },
    { header: 'Qty', dataKey: 'qty' },
    { header: 'Rate', dataKey: 'rate' },
    { header: 'CGST', dataKey: 'cgst' },
    { header: 'SGST', dataKey: 'sgst' },
    { header: 'Amount', dataKey: 'amount' }
  ];
  
  const tableData = quotation.items?.map((item, index) => ({
    sno: index + 1,
    description: item.description,
    hsn: item.hsn_code || '-',
    qty: item.quantity,
    rate: formatCurrency(item.unit_price),
    cgst: `${item.cgst_percent}%`,
    sgst: `${item.sgst_percent}%`,
    amount: formatCurrency(item.total)
  })) || [];
  
  autoTable(doc, {
    startY: y,
    head: [tableColumns.map(col => col.header)],
    body: tableData.map(row => tableColumns.map(col => row[col.dataKey])),
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: darkGray
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 15, halign: 'center' },
      6: { cellWidth: 15, halign: 'center' },
      7: { cellWidth: 25, halign: 'right' }
    },
    margin: { left: 15, right: 15 }
  });
  
  y = doc.lastAutoTable.finalY + 10;
  
  // Totals Section
  const totalsStartX = pageWidth - 80;
  
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  
  // Subtotal
  doc.text('Subtotal:', totalsStartX, y);
  doc.text(formatCurrency(quotation.subtotal), pageWidth - 15, y, { align: 'right' });
  y += 6;
  
  // Discount
  if (quotation.total_discount > 0) {
    doc.text('Discount:', totalsStartX, y);
    doc.setTextColor(220, 50, 50);
    doc.text(`-${formatCurrency(quotation.total_discount)}`, pageWidth - 15, y, { align: 'right' });
    doc.setTextColor(...darkGray);
    y += 6;
  }
  
  // GST
  doc.text(`CGST:`, totalsStartX, y);
  doc.text(formatCurrency(quotation.total_cgst), pageWidth - 15, y, { align: 'right' });
  y += 6;
  
  doc.text(`SGST:`, totalsStartX, y);
  doc.text(formatCurrency(quotation.total_sgst), pageWidth - 15, y, { align: 'right' });
  y += 6;
  
  // Total
  doc.setFillColor(...primaryColor);
  doc.rect(totalsStartX - 5, y - 3, pageWidth - totalsStartX + 5 - 10, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL:', totalsStartX, y + 4);
  doc.text(formatCurrency(quotation.net_amount), pageWidth - 15, y + 4, { align: 'right' });
  
  y += 18;
  
  // Amount in Words
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Amount in Words:', 15, y);
  doc.setFont('helvetica', 'bold');
  doc.text(numberToWords(quotation.net_amount), 15, y + 5);
  
  y += 15;
  
  // Terms & Conditions
  if (quotation.terms_conditions) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Terms & Conditions:', 15, y);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    y += 6;
    const terms = quotation.terms_conditions.split('\n');
    terms.forEach(term => {
      if (y < doc.internal.pageSize.getHeight() - 40) {
        const splitTerm = doc.splitTextToSize(term, pageWidth - 30);
        doc.text(splitTerm, 15, y);
        y += splitTerm.length * 4 + 2;
      }
    });
  }
  
  // Notes
  if (quotation.notes && y < doc.internal.pageSize.getHeight() - 40) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Notes:', 15, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    y += 5;
    const splitNotes = doc.splitTextToSize(quotation.notes, pageWidth - 30);
    doc.text(splitNotes, 15, y);
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 25;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, footerY, pageWidth - 15, footerY);
  
  doc.setFontSize(8);
  doc.setTextColor(...lightGray);
  doc.text('This quotation is valid for the period mentioned above.', pageWidth / 2, footerY + 5, { align: 'center' });
  doc.text('Prices are subject to change without prior notice.', pageWidth / 2, footerY + 10, { align: 'center' });
  
  // Signature area
  doc.setTextColor(...darkGray);
  doc.setFontSize(9);
  doc.text('For ' + (businessInfo?.business_name || 'SUNTECH ENTERPRISES'), pageWidth - 50, footerY - 15, { align: 'center' });
  doc.text('Authorized Signatory', pageWidth - 50, footerY - 5, { align: 'center' });
  
  return doc;
};

/**
 * Download PDF
 */
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

/**
 * Open PDF in new tab for preview/print
 */
export const previewPDF = (doc) => {
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
};
